import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { useInfiniteUsers } from '../hooks';
import type { User } from '../api/types';

interface UserSelectSingleProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  onUserChange?: (user: User | null) => void;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
}

export const UserSelectSingle: React.FC<UserSelectSingleProps> = ({
  value,
  onChange,
  onUserChange,
  label = 'Select User',
  placeholder = 'Search and select a user...',
  fullWidth = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUserData, setSelectedUserData] = useState<User | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteUsers({
    search: debouncedSearch,
  });

  const users = useMemo(() => data?.pages.flatMap((page) => page.data.users) ?? [], [data]);
  const selectedUser = useMemo(() => {
    if (!value) return null;
    if (selectedUserData?.id === value) return selectedUserData;
    return users.find((u) => u.id === value) ?? null;
  }, [selectedUserData, users, value]);

  const options = useMemo(() => {
    const byName = (a: User, b: User) =>
      (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, {
        sensitivity: 'base',
      });
    const sorted = [...users].sort(byName);
    if (!selectedUserData) return sorted;
    // Keep the selected user pinned at the top, the rest sorted alphabetically.
    const rest = sorted.filter((u) => u.id !== selectedUserData.id);
    return [selectedUserData, ...rest];
  }, [selectedUserData, users]);

  const handleListboxScroll = (event: React.UIEvent<HTMLElement>) => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (!value) {
      setSelectedUserData(null);
      onUserChange?.(null);
      return;
    }
    if (selectedUserData?.id === value) return;
    const found = users.find((u) => u.id === value);
    if (found) {
      setSelectedUserData(found);
      onUserChange?.(found);
    }
  }, [onUserChange, selectedUserData?.id, users, value]);

  if (error) {
    return <Alert severity='error'>Failed to load users. Please try again.</Alert>;
  }

  return (
    <Box>
      <Autocomplete
        fullWidth={fullWidth}
        options={options}
        value={selectedUser}
        onChange={(_event, newValue: User | null) => {
          setSelectedUserData(newValue);
          onUserChange?.(newValue);
          onChange(newValue?.id ?? null);
        }}
        inputValue={searchTerm}
        onInputChange={(_event, newInputValue, reason) => {
          // Clear search on focus to show all users
          if (reason === 'reset') {
            setSearchTerm('');
          } else {
            setSearchTerm(newInputValue);
          }
        }}
        onFocus={() => setSearchTerm('')}
        getOptionLabel={(option) => option.name || option.email}
        loading={isLoading || isFetchingNextPage}
        slotProps={{ listbox: { onScroll: handleListboxScroll } }}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        blurOnSelect
        clearOnBlur
        openOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            size='small'
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
